import { Select, Spin, Tooltip } from 'antd'
import { useEffect, useRef, useState } from 'react'

import { restClient } from '@/Core'
import { buildOptionsFilters } from '@/Ship/Templates/CrudResource/Components/buildOptionsFilters'

import type { ReportOption } from '../Contracts/Report'
import type { ReportOptionSource } from '../Helpers/reportSources'

interface ReportEntitySelectProps {
    source: ReportOptionSource
    value: ReportOption[]
    onChange: (value: ReportOption[]) => void
    id?: string
}

/** Пауза між введенням у пошук і запитом до API */
const SEARCH_DEBOUNCE_MS = 300
/** Скільки записів показувати у списку без пошуку і за пошуком */
const OPTIONS_LIMIT = 30

/**
 * Множинний вибір записів довідника для фільтра звіту: опції з API з пошуком, значення —
 * пари {id, підпис}, тож підписи вибраних тегів не губляться між пошуками.
 */
export const ReportEntitySelect = ({ source, value, onChange, id }: ReportEntitySelectProps) => {
    const [options, setOptions] = useState<ReportOption[]>([])
    const [loading, setLoading] = useState(false)
    const abortRef = useRef<AbortController | null>(null)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(
        () => () => {
            abortRef.current?.abort()
            if (timerRef.current) clearTimeout(timerRef.current)
        },
        []
    )

    const load = async (query: string) => {
        abortRef.current?.abort()
        const controller = new AbortController()
        abortRef.current = controller
        setLoading(true)

        const searchParams: Record<string, string | number> = { limit: OPTIONS_LIMIT }
        if (query) searchParams['q'] = query
        const filters = buildOptionsFilters(source.filter)
        if (filters) searchParams['filters'] = filters

        try {
            const data: any = await restClient.get(
                source.url,
                { searchParams, signal: controller.signal },
                { isDisableResponseCache: true }
            )
            const list: Record<string, any>[] = Array.isArray(data?.items) ? data.items : []
            setOptions(
                list
                    .filter((item) => typeof item['id'] === 'number')
                    .map((item) => ({ value: item['id'] as number, label: source.optionLabel(item) }))
            )
        } catch (error) {
            // Перерваний запит — нормальна ситуація при швидкому введенні
            if ((error as Error)?.name !== 'AbortError') console.error('ReportEntitySelect:', error)
        } finally {
            if (abortRef.current === controller) setLoading(false)
        }
    }

    const scheduleSearch = (query: string) => {
        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => void load(query.trim()), SEARCH_DEBOUNCE_MS)
    }

    // Вибрані — завжди серед опцій (підпис тегу), далі результати поточного запиту
    const selectedIds = new Set(value.map((option) => option.value))
    const merged = [...value, ...options.filter((option) => !selectedIds.has(option.value))]

    return (
        <Select<ReportOption[]>
            {...(id ? { id } : {})}
            mode="multiple"
            labelInValue
            value={value}
            onChange={(next) => onChange(next.map((option) => ({ value: option.value, label: String(option.label) })))}
            options={merged}
            allowClear
            showSearch
            filterOption={false}
            onSearch={scheduleSearch}
            onOpenChange={(open) => {
                if (open) void load('')
            }}
            notFoundContent={loading ? <Spin size="small" /> : 'Нічого не знайдено'}
            placeholder="Усі"
            // Довгі назви ширші за поле фільтра: список не вужчий за 300px
            popupMatchSelectWidth={300}
            maxTagCount="responsive"
            maxTagPlaceholder={(omitted) => (
                <Tooltip title={omitted.map((tag) => tag.label).join(', ')}>
                    <span>+{omitted.length}</span>
                </Tooltip>
            )}
            style={{ width: '100%' }}
        />
    )
}
